import React, { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Clock,
  Users,
  Video,
  Sparkles,
  CheckCircle2,
  Loader2,
  X,
  AlertCircle,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { databaseService } from "../services/database";
import { toast } from "sonner";
import { tr } from '../i18n/text';
import { useLeadGuard } from '../hooks/useLeadGuard';

interface ScheduleDemoProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onNavigateToLogin?: () => void;
}

export default function ScheduleDemo({
  isOpen,
  onClose,
  onSuccess,
  onNavigateToLogin,
}: ScheduleDemoProps) {
  const guard = useLeadGuard();
  useEffect(() => {
    if (isOpen) guard.reset();
  }, [isOpen, guard.reset]);
  const { resolvedTheme } = useTheme();
  const { t } = useLanguage();
  const isDark = resolvedTheme === "dark";

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    business_type: "",
    phone_number: "",
    preferred_time: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [demoId, setDemoId] = useState("");

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!formData.full_name || !formData.email || !formData.business_type) {
        setError(t('contactUsPage.errors.required', { defaultValue: "Please fill in all required fields" }));
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError(t('contactUsPage.errors.invalidEmail', { defaultValue: "Please enter a valid email address" }));
        return;
      }

      const response = await databaseService.submitDemoRequest({
        ...guard.read(),
        ...formData,
        company_name: "",
        trainer_count: "",
        message: "",
        specific_interests: [],
        utm_source: "demo_modal",
        utm_medium: "website",
      });

      if (response.success) {
        setSuccess(true);
        setDemoId(response.demoId);
        setFormData({ full_name: "", email: "", business_type: "", phone_number: "", preferred_time: "" });
        toast.success(t('scheduleDemo.successHeader', { defaultValue: "Demo request submitted successfully!" }));
        setTimeout(() => {
          setSuccess(false);
          if (onSuccess) onSuccess();
          onClose();
        }, 5000);
      } else {
        setError(response.error || t('contactUsPage.errors.failed', { defaultValue: "Failed to submit demo request" }));
        toast.error(t('contactUsPage.errors.failed', { defaultValue: "Failed to submit demo request" }));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('contactUsPage.errors.unexpected', { defaultValue: "An unexpected error occurred." });
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ full_name: "", email: "", business_type: "", phone_number: "", preferred_time: "" });
    setError("");
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // ── shared input style ──────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "12px",
    border: "1px solid var(--border-color)",
    backgroundColor: "var(--bg-tertiary)",
    color: "var(--text-primary)",
    fontSize: "14px",
    fontFamily: "inherit",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "13px",
    fontWeight: 500,
    marginBottom: "6px",
    color: "var(--text-secondary)",
  };

  const mutedLabelStyle: React.CSSProperties = {
    ...labelStyle,
    color: "var(--text-muted)",
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto p-0 border-0 shadow-none bg-transparent"
        style={{ outline: "none" }}
      >
        {/* Modal shell */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            backgroundColor: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            boxShadow: isDark
              ? "var(--shadow-lg), 0 0 60px rgba(99,102,241,0.15)"
              : "var(--shadow-lg)",
          }}
        >
          {/* ── Header ── */}
          <div
            className="relative px-6 pt-6 pb-5"
            style={{ borderBottom: "1px solid var(--border-color)" }}
          >
            {/* Gradient accent top strip */}
            <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
              style={{ background: "linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)" }}
            />

            {/* Custom close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150"
              style={{
                backgroundColor: "var(--bg-tertiary)",
                color: "var(--text-muted)",
                border: "1px solid var(--border-color)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-indigo)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = "var(--text-muted)";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--border-color)";
              }}
              aria-label={tr("Close")}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center pr-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-3"
                style={{
                  backgroundColor: "rgba(99,102,241,0.1)",
                  color: "var(--accent-indigo)",
                  border: "1px solid rgba(99,102,241,0.2)",
                }}>
                <Sparkles className="w-3 h-3" />
                {t('scheduleDemo.badge', { defaultValue: "Free 30-min demo" })}
              </div>
              <h2
                className="text-xl font-bold mb-1.5"
                style={{ color: "var(--text-primary)" }}
              >
                {t('scheduleDemo.title', { defaultValue: "Schedule Your Personal Demo" })}
              </h2>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                {t('scheduleDemo.subtitle', { defaultValue: "Get a personalized walk-through of TrainZilla tailored to your fitness business." })}
              </p>
            </div>

            {/* Demo info pills */}
            <div className="flex items-center justify-center gap-4 mt-4">
              {[
                { icon: <Clock className="w-3.5 h-3.5" />, label: t('scheduleDemo.pillTime', { defaultValue: "30 minutes" }) },
                { icon: <Video className="w-3.5 h-3.5" />, label: t('scheduleDemo.pillLive', { defaultValue: "Live demo" }) },
                { icon: <Users className="w-3.5 h-3.5" />, label: t('scheduleDemo.pillPersonalized', { defaultValue: "Personalized" }) },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium"
                  style={{
                    backgroundColor: "var(--bg-tertiary)",
                    color: "var(--text-secondary)",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <span style={{ color: "var(--accent-indigo)" }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          </div>

          {/* ── Body ── */}
          <div className="px-6 py-5">
            {success ? (
              /* Success state */
              <div className="text-center py-6" role="region" aria-labelledby="success-heading">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: "rgba(16,185,129,0.12)",
                    border: "1px solid rgba(16,185,129,0.25)",
                  }}
                >
                  <CheckCircle2 className="w-8 h-8" style={{ color: "var(--accent-emerald)" }} />
                </div>
                <h3
                  id="success-heading"
                  className="text-lg font-bold mb-2"
                  style={{ color: "var(--text-primary)" }}
                >
                  {t('scheduleDemo.successHeader', { defaultValue: "Demo Scheduled! 🎉" })}
                </h3>
                <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
                  {t('scheduleDemo.successDesc', { defaultValue: "Our team will contact you within 24 hours to confirm your demo time." })}
                </p>
                {demoId && (
                  <div
                    className="rounded-xl px-4 py-3 mb-5 text-left"
                    style={{
                      backgroundColor: "rgba(99,102,241,0.08)",
                      border: "1px solid rgba(99,102,241,0.2)",
                    }}
                  >
                    <p className="text-xs font-medium" style={{ color: "var(--accent-indigo)" }}>
                      {t('scheduleDemo.requestId', { defaultValue: "Request ID" })}: <span className="font-mono">{demoId}</span>
                    </p>
                  </div>
                )}
                <div className="flex justify-center gap-3">
                  <button
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                    style={{
                      backgroundColor: "var(--bg-tertiary)",
                      border: "1px solid var(--border-color)",
                      color: "var(--text-primary)",
                    }}
                  >
                    {t('common.actions.close', { defaultValue: "Close" })}
                  </button>
                  {onNavigateToLogin && (
                    <button
                      onClick={() => { onNavigateToLogin(); handleClose(); }}
                      className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5"
                      style={{
                        background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                        boxShadow: "0 4px 14px rgba(99,102,241,0.4)",
                      }}
                    >
                      {t('scheduleDemo.signUpNow', { defaultValue: "Sign Up Now" })}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="space-y-4" role="form" aria-label={tr("Demo request form")}>
{guard.field}

                {/* Error banner */}
                {error && (
                  <div
                    className="flex items-start gap-3 rounded-xl px-4 py-3"
                    role="alert"
                    style={{
                      backgroundColor: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.2)",
                    }}
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
                    <p className="text-sm" style={{ color: isDark ? "#fca5a5" : "#dc2626" }}>
                      {error}
                    </p>
                  </div>
                )}

                {/* Full Name */}
                <div>
                  <label htmlFor="demo_full_name" style={labelStyle}>
                    {t('scheduleDemo.fullName', { defaultValue: "Full Name" })} <span style={{ color: "#ef4444" }} aria-label="required">*</span>
                  </label>
                  <input
                    id="demo_full_name"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange("full_name", e.target.value)}
                    placeholder={t('scheduleDemo.fullNamePlaceholder', { defaultValue: "Your full name" })}
                    required
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = "var(--accent-indigo)";
                      e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "var(--border-color)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="demo_email" style={labelStyle}>
                    {t('scheduleDemo.email', { defaultValue: "Email" })} <span style={{ color: "#ef4444" }} aria-label="required">*</span>
                  </label>
                  <input
                    id="demo_email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder={t('scheduleDemo.emailPlaceholder', { defaultValue: "your.email@company.com" })}
                    required
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = "var(--accent-indigo)";
                      e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "var(--border-color)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                {/* Business Type */}
                <div>
                  <label htmlFor="demo_business_type" style={labelStyle}>
                    {t('scheduleDemo.businessType', { defaultValue: "Business Type" })} <span style={{ color: "#ef4444" }} aria-label="required">*</span>
                  </label>
                  <Select
                    value={formData.business_type}
                    onValueChange={(value) => handleInputChange("business_type", value)}
                  >
                    <SelectTrigger
                      className="mt-0 w-full rounded-xl"
                      aria-label={t('scheduleDemo.businessType', { defaultValue: "Business Type" })}
                      style={{
                        backgroundColor: "var(--bg-tertiary)",
                        borderColor: "var(--border-color)",
                        color: formData.business_type ? "var(--text-primary)" : "var(--text-muted)",
                        height: "42px",
                        fontSize: "14px",
                      }}
                    >
                      <SelectValue placeholder={t('scheduleDemo.businessPlaceholder', { defaultValue: "Select business type" })} />
                    </SelectTrigger>
                    <SelectContent
                      style={{
                        backgroundColor: "var(--bg-card)",
                        border: "1px solid var(--border-color)",
                        color: "var(--text-primary)",
                        borderRadius: "12px",
                      }}
                    >
                      {[
                        ["personal_trainer", t('scheduleDemo.businessTypes.personal', { defaultValue: "Personal Trainer" })],
                        ["gym_owner", t('scheduleDemo.businessTypes.gym', { defaultValue: "Gym Owner" })],
                        ["fitness_studio", t('scheduleDemo.businessTypes.studio', { defaultValue: "Fitness Studio" })],
                        ["wellness_center", t('scheduleDemo.businessTypes.wellness', { defaultValue: "Wellness Center" })],
                        ["corporate_fitness", t('scheduleDemo.businessTypes.corporate', { defaultValue: "Corporate Fitness" })],
                        ["other", t('scheduleDemo.businessTypes.other', { defaultValue: "Other" })],
                      ].map(([val, label]) => (
                        <SelectItem
                          key={val}
                          value={val}
                          className="cursor-pointer"
                          style={{ color: "var(--text-primary)", fontSize: "14px" }}
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Row: Phone + Preferred Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="demo_phone" style={mutedLabelStyle}>
                      {t('scheduleDemo.phone', { defaultValue: "Phone" })} <span style={{ fontSize: "11px" }}>{t('scheduleDemo.optional', { defaultValue: "(Optional)" })}</span>
                    </label>
                    <input
                      id="demo_phone"
                      value={formData.phone_number}
                      onChange={(e) => handleInputChange("phone_number", e.target.value)}
                      placeholder="+91 84596 91471"
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = "var(--accent-indigo)";
                        e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "var(--border-color)";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  <div>
                    <label style={mutedLabelStyle}>
                      {t('scheduleDemo.preferredTime', { defaultValue: "Preferred Time" })} <span style={{ fontSize: "11px" }}>{t('scheduleDemo.optional', { defaultValue: "(Optional)" })}</span>
                    </label>
                    <Select
                      value={formData.preferred_time}
                      onValueChange={(value) => handleInputChange("preferred_time", value)}
                    >
                      <SelectTrigger
                        className="w-full rounded-xl"
                        aria-label={t('scheduleDemo.preferredTime', { defaultValue: "Preferred Time" })}
                        style={{
                          backgroundColor: "var(--bg-tertiary)",
                          borderColor: "var(--border-color)",
                          color: formData.preferred_time ? "var(--text-primary)" : "var(--text-muted)",
                          height: "42px",
                          fontSize: "14px",
                        }}
                      >
                        <SelectValue placeholder={t('scheduleDemo.timePlaceholder', { defaultValue: "Any time" })} />
                      </SelectTrigger>
                      <SelectContent
                        style={{
                          backgroundColor: "var(--bg-card)",
                          border: "1px solid var(--border-color)",
                          color: "var(--text-primary)",
                          borderRadius: "12px",
                        }}
                      >
                        {[
                          ["morning", t('scheduleDemo.timeSlots.morning', { defaultValue: "Morning (9 AM – 12 PM)" })],
                          ["afternoon", t('scheduleDemo.timeSlots.afternoon', { defaultValue: "Afternoon (12 PM – 5 PM)" })],
                          ["evening", t('scheduleDemo.timeSlots.evening', { defaultValue: "Evening (5 PM – 8 PM)" })],
                          ["flexible", t('scheduleDemo.timeSlots.flexible', { defaultValue: "Any time works" })],
                        ].map(([val, label]) => (
                          <SelectItem
                            key={val}
                            value={val}
                            className="cursor-pointer"
                            style={{ color: "var(--text-primary)", fontSize: "14px" }}
                          >
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                    style={{
                      backgroundColor: "var(--bg-tertiary)",
                      border: "1px solid var(--border-color)",
                      color: "var(--text-secondary)",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-indigo)";
                      (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = "var(--border-color)";
                      (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)";
                    }}
                  >
                    {t('common.actions.cancel', { defaultValue: "Cancel" })}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    style={{
                      background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      boxShadow: "0 4px 14px rgba(99,102,241,0.4)",
                    }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('scheduleDemo.scheduling', { defaultValue: "Scheduling…" })}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        {t('scheduleDemo.submitButton', { defaultValue: "Schedule Demo" })}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}